<template>
  <div
    class="featured-podcast pointer has-edit-button"
    :class="featuredPodcast.theme_size"
  >
    <!-- Show only image for size "full" (mobile screen only) -->
    <AppImg
      v-if="featuredPodcast.theme_size === 'full' && !isDesktopScreen"
      :img-src="image?.url"
      img-alt="featured podcast image"
      border-radius="10px"
      class="img-wrap"
    />

    <!-- Show card for other sizes -->
    <ion-card v-else :style="getBackgroundColor" mode="ios">
      <EditButton
        v-if="canEdit"
        :url="`featured-course/${featuredPodcast.id}/edit`"
      />

      <AppImg
        :img-src="image?.url"
        img-alt="featured podcast image"
        border-radius="10px"
        class="img-wrap"
      />

      <ion-card-header v-if="displayInfo">
        <ion-card-subtitle
          class="card-subtitle fz-10 fz-12-lg fw-300 ion-no-margin"
        >
          {{ featuredPodcast.company_name }}
        </ion-card-subtitle>
        <ion-card-title class="card-title fz-12 fz-14-lg fw-500">
          {{ featuredPodcast.main_title }}
        </ion-card-title>
      </ion-card-header>

      <ion-card-content>
        <div
          class="add-icon-with-fireworks-animation-wrapper"
          @click.stop="handleAddClick"
        >
          <ion-icon
            :icon="addIcon"
            size="large"
            class="clickable-icon"
          ></ion-icon>

          <!-- Animation -->
          <template v-if="showAddAnimation">
            <div v-for="particle in 12" :key="particle" class="particle" />
          </template>
        </div>

        <ion-icon :icon="playCircle" size="large"></ion-icon>
      </ion-card-content>
    </ion-card>
  </div>
</template>

<script setup>
import AppImg from '@/components/common/AppImg.vue';
import EditButton from '@/components/common/EditButton.vue';
import { getSmallestSizeImage } from '@/helpers/functions';

import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonIcon,
} from '@ionic/vue';
import { addCircleOutline, checkmarkCircle, playCircle } from 'ionicons/icons';
import { ref, computed } from '@vue/reactivity';
import { useStore } from 'vuex';

import useBackgroundColor from '@/composables/useBackgroundColor';
import useFavorites from '@/composables/useFavorites';
import useDeviceScreen from '@/composables/useDeviceScreen';

defineOptions({
  name: 'FeaturedPodcast',
});

const props = defineProps({
  featuredPodcast: {
    type: Object,
    required: true,
  },
  displayInfo: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();
const { isDesktopScreen } = useDeviceScreen();

const image = computed(
  () => getSmallestSizeImage(props.featuredPodcast.media) || null,
);

const canEdit = computed(() => {
  const hasPermission = store.getters['authentication/hasPermission'](
    'update_featured_course',
  );
  const isWeb = store.state.app.platform === 'web';
  return hasPermission && isWeb;
});

const { getLightColor } = useBackgroundColor();
const getBackgroundColor = computed(() => {
  const allowedThemes = ['xl', 'full'];
  if (!allowedThemes.includes(props.featuredPodcast.theme_size)) {
    return {
      '--background': 'none',
    };
  }

  const palette = image.value?.vibrant_palette_json;
  if (!palette) {
    return {
      '--background': '#dfe0e2',
    };
  }

  return {
    '--background': getLightColor(palette),
  };
});

const { isFavorite, toggleFavourite } = useFavorites();

const isAdded = computed(() =>
  isFavorite(
    props.featuredPodcast.course_id,
    '',
    props.featuredPodcast.entity_type,
  ),
);

const addIcon = computed(() =>
  isAdded.value ? checkmarkCircle : addCircleOutline,
);

const showAddAnimation = ref(false);
async function handleAddClick() {
  try {
    const wasAdded = isAdded.value;

    await toggleFavourite(
      props.featuredPodcast.course_id,
      '',
      props.featuredPodcast.entity_type,
    );

    if (!wasAdded) {
      showAddAnimation.value = true;
      setTimeout(() => {
        showAddAnimation.value = false;
      }, 2000);
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
  }
}
</script>

<style scoped lang="scss">
@use '@/theme/custom' as *;
.featured-podcast {
  width: 140px;

  ion-card {
    margin: 10px 10px 24px 0px;

    .img-wrap {
      width: 120px;
      height: 120px;
      overflow: hidden;
      border-radius: 10px;

      &:deep(img) {
        width: 100%;
        height: 100%;
      }
    }

    ion-card-header {
      padding-left: 0;
      padding-right: 0;
      padding-top: 8px;
      padding-bottom: 0px;

      .card-title {
        overflow: hidden;
        line-height: 1.5em;
        max-height: 1.5em;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .card-subtitle {
        text-transform: none;
        --color: #fff;
      }
    }

    ion-card-content {
      display: none;
    }

    @media (max-width: $tablet-breakpoint) {
      margin: 10px 10px 16px 0px;
    }
  }

  // Small styles
  &.small {
    width: 116px;

    .img-wrap {
      width: 100px;
      height: 100px;
    }

    .card-subtitle {
      display: none;
    }
  }

  // Large styles
  &.large {
    width: 210px;

    .img-wrap {
      width: 200px;
      height: 200px;
    }

    @media (max-width: $tablet-breakpoint) {
      width: 166px;

      .img-wrap {
        width: 150px;
        height: 150px;
      }
    }
  }

  // XL styles
  &.xl {
    width: 70vw;
    max-width: 350px;

    ion-card {
      background-color: var(--background);
      padding: 30px 10px 5px 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      height: 380px;

      .img-wrap {
        width: 240px;
        height: 240px;

        @media (max-width: $tablet-breakpoint) {
          width: 200px;
          height: 200px;
        }
      }

      ion-card-header {
        width: 100%;

        .card-title {
          font-size: 18px;
          font-weight: 700;
          color: black;
        }

        .card-subtitle {
          font-size: 13px;
          font-weight: 700;
          color: #5e5e5e;
        }
      }

      ion-card-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 0;
        margin-top: auto;
      }

      @media (max-width: $tablet-breakpoint) {
        height: 340px;
      }
    }
  }

  // Full styles
  &.full {
    @extend .xl;

    @media (max-width: $tablet-breakpoint) {
      width: 65vw;
      max-width: 350px;

      .img-wrap {
        width: 270px;
        height: 270px;

        :deep(img) {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}
</style>
